import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types (SQLite, with JOINed fields) ───

export interface QueueEntryRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_number: string | null;
  position: number | null;
  queue_date: string | null;
  source: string | null; // 'walk-in' | 'appointment'
  status: string | null; // 'expected' | 'waiting' | 'called' | 'serving' | 'done'
  priority: string | null; // 'normal' | 'urgent' | 'vip'
  provider_id: string | null;
  service_id: string | null;
  notes: string | null;
  tags: string | null; // JSON string on client
  arrived_at: string | null;
  called_at: string | null;
  serving_at: string | null;
  done_at: string | null;
  initial_fee_paid_at: string | null; // PRD.288 — set after Initial Fee invoice paid
  created_at: string | null;
  updated_at: string | null;
  // Joined fields
  patient_name: string | null;
  patient_photo_url: string | null;
  provider_name: string | null;
  provider_avatar_color: string | null;
  service_name: string | null;
  appointment_start_time: string | null;
}

// ─── Queries ───

/**
 * Fetch today's queue with patient/provider/service details via JOINs.
 *
 * PRD.293 — pass `providerId` to scope the queue to a single provider (doctor's
 * own queue). Omitted → clinic-wide (unchanged). Rows with a NULL provider_id
 * are excluded when scoped, matching the "unassigned records hidden" rule.
 */
export function qQueueToday(queueDate: string, providerId?: string): QuerySpec<string[]> {
  const providerClause = providerId ? 'AND q.provider_id = ?' : '';
  return {
    key: ['queue_entries', 'today', queueDate, providerId ?? ''] as const,
    sql: `
      SELECT
        q.*,
        p.full_name   AS patient_name,
        p.photo_url   AS patient_photo_url,
        ap.display_name AS provider_name,
        ap.avatar_color AS provider_avatar_color,
        asvc.name       AS service_name,
        a.start_time    AS appointment_start_time
      FROM queue_entries q
      LEFT JOIN patients p             ON p.id = q.patient_id
      LEFT JOIN appointment_providers ap ON ap.id = q.provider_id
      LEFT JOIN appointment_services asvc ON asvc.id = q.service_id
      LEFT JOIN appointments a         ON a.id = q.appointment_id
      WHERE q.queue_date = ? ${providerClause}
      ORDER BY
        CASE q.status
          WHEN 'called'   THEN 0
          WHEN 'waiting'  THEN 1
          WHEN 'serving'  THEN 2
          WHEN 'expected' THEN 3
          WHEN 'done'     THEN 4
        END,
        q.position ASC
    `,
    params: providerId ? [queueDate, providerId] : [queueDate],
    watch: [
      'queue_entries',
      'patients',
      'appointment_providers',
      'appointment_services',
      'appointments',
    ],
  };
}

// ─── Row type for queued appointment IDs lookup ───

export interface QueuedAppointmentRow {
  appointment_id: string;
  queue_date: string;
}

/**
 * Fetch appointment_ids that are already queued for a given date.
 * Used to disable "already queued" items in the Add from Appointment drawer.
 */
export function qQueuedAppointmentIds(queueDate: string): QuerySpec<[string]> {
  return {
    key: ['queue_entries', 'queued-appts', queueDate] as const,
    sql: `SELECT appointment_id, queue_date FROM queue_entries WHERE queue_date = ? AND appointment_id IS NOT NULL`,
    params: [queueDate],
    watch: ['queue_entries'],
  };
}

/** Row shape for Today's Patients — distinct patients from queue with details. */
export interface TodayQueuePatientRow {
  id: string; // patient id
  patient_number: string | null;
  full_name: string | null;
  birth_date: string | null;
  // PRD.315 — reported-age fallback for patients with no birth date.
  age_reported: number | null;
  age_reported_at: string | null;
  sex: string | null;
  photo_url: string | null;
  last_visited_at: string | null;
  id_type: string | null;
  id_value: string | null;
  is_favorite: number | null; // 1 or null
  queue_status: string | null; // latest queue status for this patient today
}

/**
 * Distinct patients queued today, joined with identifiers and favorite status.
 * For patients with multiple queue entries, picks the one with highest priority status.
 */
export function qTodayQueuePatients(
  queueDate: string,
  userId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['queue_entries', 'today-patients', queueDate, userId] as const,
    sql: `
      SELECT
        p.id, p.patient_number, p.full_name, p.birth_date,
        p.age_reported, p.age_reported_at, p.sex, p.photo_url,
        p.last_visited_at,
        pi.id_type, pi.id_value,
        (fav.id IS NOT NULL) as is_favorite,
        q.status AS queue_status
      FROM queue_entries q
      INNER JOIN patients p ON p.id = q.patient_id
      LEFT JOIN patient_identifiers pi
        ON pi.id = (
          SELECT id FROM patient_identifiers
          WHERE patient_id = p.id
          ORDER BY id LIMIT 1
        )
      LEFT JOIN user_patient_favorites fav
        ON fav.patient_id = p.id AND fav.user_id = ?
      WHERE q.queue_date = ?
        AND q.id = (
          SELECT q2.id FROM queue_entries q2
          WHERE q2.patient_id = q.patient_id AND q2.queue_date = q.queue_date
          ORDER BY
            CASE q2.status
              WHEN 'serving' THEN 0
              WHEN 'called'  THEN 1
              WHEN 'waiting' THEN 2
              WHEN 'expected' THEN 3
              WHEN 'done'    THEN 4
            END,
            q2.position ASC
          LIMIT 1
        )
      ORDER BY
        CASE q.status
          WHEN 'serving'  THEN 0
          WHEN 'called'   THEN 1
          WHEN 'waiting'  THEN 2
          WHEN 'expected' THEN 3
          WHEN 'done'     THEN 4
        END,
        q.position ASC
    `,
    params: [userId, queueDate],
    watch: ['queue_entries', 'patients', 'patient_identifiers', 'user_patient_favorites'],
  };
}

/**
 * Get the maximum queue number for a given date to compute the next number.
 */
export function qNextQueueNumber(queueDate: string): QuerySpec<[string]> {
  return {
    key: ['queue_entries', 'next-number', queueDate] as const,
    sql: `SELECT MAX(CAST(SUBSTR(queue_number, 3) AS INTEGER)) AS max_num FROM queue_entries WHERE queue_date = ?`,
    params: [queueDate],
    watch: ['queue_entries'],
  };
}
